import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SITE_CONFIG, getExchangeHref } from '@/config/site';
import { resolveBrandLogoUrl, BRAND_LOGO } from '@/lib/brandAssets';

const LOGO_URL = resolveBrandLogoUrl(SITE_CONFIG.brandLogoUrl);

const NAV_LINKS = [
  { name: 'About',      href: '/about',      isPage: true  },
  { name: 'Transparency', href: '/about#transparency', isPage: true },
  { name: 'Utility',    href: '#utility',    isPage: false },
  { name: 'Exchange',   href: '#exchange',   isPage: false },
  { name: 'KYC',        href: '#instant-kyc', isPage: false },
  { name: 'INR',        href: '#inr-fiat',   isPage: false },
  { name: 'Roadmap',    href: '#roadmap',    isPage: false },
  { name: 'Tokenomics', href: '#tokenomics', isPage: false },
  { name: 'Whitepaper', href: '/whitepaper', isPage: true  },
  { name: 'FAQ',        href: '#faq',        isPage: false },
];

export const Navbar = () => {
  const [scrolled,    setScrolled]    = useState(false);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();

  /* Close mobile menu on route change */
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Scroll to hash or navigate to page */
  const handleLink = (link) => {
    setMenuOpen(false);
    if (link.isPage) {
      navigate(link.href);
      return;
    }
    if (location.pathname !== '/') {
      /* Navigate to home, then scroll after the page mounts */
      navigate('/');
      setTimeout(() => {
        document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /* Which link is "active" */
  const isActive = (link) => {
    if (link.isPage) return location.pathname === link.href;
    return false; /* hash links don't have a stable "active" outside IntersectionObserver */
  };

  return (
    /* Single fixed element — banner + nav bar together */
    <motion.header
      data-testid="navbar"
      initial={{ y: -120 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50"
    >

      {/* ── Main navigation bar ───────────────────────────────────── */}
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? 'bg-[#0a0b0d]/90 backdrop-blur-xl border-b border-[#9C7941]/10 py-3'
            : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 xl:px-16 flex items-center gap-4">

          {/* Logo */}
          <motion.a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              if (location.pathname !== '/') navigate('/');
              else window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2.5 flex-shrink-0 group"
            whileHover={{ scale: 1.02 }}
            data-testid="navbar-logo"
          >
            <img
              src={LOGO_URL}
              alt="BITZX"
              className="h-9 w-9 sm:h-10 sm:w-10 object-contain transition-transform group-hover:scale-105"
              onError={(e) => {
                if (e.currentTarget.src === BRAND_LOGO) return;
                e.currentTarget.onerror = null;
                e.currentTarget.src = BRAND_LOGO;
              }}
            />
            <span className="text-lg sm:text-xl font-bold tracking-wide leading-none">
              <span className="text-white">BITZ</span>
              <span className="text-[#EBD38D]">X</span>
            </span>
          </motion.a>

          {/* Desktop nav links — lg and above */}
          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {NAV_LINKS.map((link) => (
              <motion.button
                key={link.name}
                onClick={() => handleLink(link)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link)
                    ? 'text-[#EBD38D] bg-[#9C7941]/10'
                    : 'text-[#C5C6CB] hover:text-[#EBD38D] hover:bg-[#9C7941]/8'
                }`}
                whileHover={{ y: -1 }}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </motion.button>
            ))}
          </nav>

          {/* Spacer pushes right CTAs to the edge */}
          <div className="flex-1" />

          {/* Right CTAs — always present, scaling with viewport */}
          <div className="flex items-center gap-2 sm:gap-3">
            <motion.a
              href={getExchangeHref()}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center text-xs sm:text-sm font-bold px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border-2 border-[#9C7941]/30 text-[#EBD38D] bg-[#9C7941]/8 whitespace-nowrap"
              whileHover={{ scale: 1.04, borderColor: 'rgba(235,211,141,0.5)' }}
              whileTap={{ scale: 0.97 }}
              data-testid="navbar-exchange-btn"
            >
              Trade
            </motion.a>
            {/* Buy $BZX — always visible */}
            <motion.a
              href={SITE_CONFIG.buyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xs sm:text-sm font-bold px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-[#9C7941] to-[#EBD38D] text-[#0a0b0d] shadow-[0_0_20px_rgba(156,121,65,0.3)] whitespace-nowrap"
              whileHover={{ scale: 1.04, boxShadow: '0 0 28px rgba(156,121,65,0.5)' }}
              whileTap={{ scale: 0.97 }}
              data-testid="navbar-buy-btn"
            >
              Buy $BZX
            </motion.a>

            {/* Hamburger — visible only when desktop nav is hidden (< lg) */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg text-[#D5D5D0] hover:text-white hover:bg-[#1e2028] transition-colors"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              data-testid="mobile-menu-btn"
            >
              <AnimatePresence mode="wait" initial={false}>
                {menuOpen
                  ? <motion.span key="x"    initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}><X    size={20} /></motion.span>
                  : <motion.span key="menu" initial={{ rotate:  90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}><Menu size={20} /></motion.span>
                }
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile / Tablet drawer (< lg) ─────────────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-[#0d0f14]/95 backdrop-blur-xl border-b border-[#9C7941]/10"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">

              {/* Nav links */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mb-4">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => handleLink(link)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${
                      isActive(link)
                        ? 'text-[#EBD38D] bg-[#9C7941]/15'
                        : 'text-[#C5C6CB] hover:text-[#EBD38D] hover:bg-[#1e2028]'
                    }`}
                  >
                    <ChevronRight size={14} className="text-[#9C7941] flex-shrink-0" />
                    {link.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
